import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import FormInput from 'components/Forms/FormInput';
import { UniqueHashTypeEnum, User, useCreateOrganisationCustomerPasswordMutation } from 'generated/graphql';
import { FormProvider, useForm } from 'react-hook-form';

import FormError from 'components/Forms/FormError';
import useNotificationBar from 'hooks/useNotificationBar';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { useTranslatedCreatePasswordSchema } from './hooks/useTranslatedCreatePasswordSchema';
import { CreatePasswordSchemaType } from './types/CreatePasswordSchemaType';
import { createPasswordFormDefaultValues } from './data/createPasswordFormDefaultValues';

export type CreatePasswordFormProps = {
    email?: string;
    hash: string;
    hashType: UniqueHashTypeEnum;
    onComplete?: () => void;
};

export function CreatePasswordForm({ email, hash, hashType, onComplete }: CreatePasswordFormProps) {
    const schema = useTranslatedCreatePasswordSchema();
    const { t } = useServerTranslation();
    const { showSuccess, showError } = useNotificationBar();

    const methods = useForm<CreatePasswordSchemaType>({
        defaultValues: createPasswordFormDefaultValues,
        resolver: zodResolver(schema),
    });

    const [createPassword, { loading, error }] = useCreateOrganisationCustomerPasswordMutation({
        onCompleted: (data) => {
            if (data.createOrganisationCustomerPassword?.success) {
                showSuccess(t('user.create-password.success'));
                onComplete?.();

                return;
            }
            showError(t('user.create-password.error'));
        },
        onError: () => {
            showError(t('user.create-password.error'));
        },
    });

    const { handleSubmit } = methods;

    if (!email) {
        return (
            <Typography textAlign="center" variant="h1" paddingY={40}>
                {t('user.create-password.could-not-find-user')}
            </Typography>
        );
    }

    const onSubmit = ({ password, password_again }: CreatePasswordSchemaType) => {
        if (password !== password_again) {
            methods.setError('password_again', { message: t('user.create-password.passwords-dont-match') });

            return;
        }
        createPassword({
            variables: {
                hash,
                hashType,
                password,
            },
        });
    };

    return (
        <Stack gap={8} paddingBottom={8}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box display="flex" justifyContent="center">
                        <Box maxWidth={500}>
                            <FormInput
                                label={t('form-fields.user.password')}
                                name="password"
                                type="password"
                                variant="outlined"
                            />
                            <FormInput
                                label={t('form-fields.user.password-again')}
                                name="password_again"
                                type="password"
                                variant="outlined"
                            />
                            <FormError show={Boolean(error?.message)}>
                                {t('user.create-password.error')}
                            </FormError>
                            {loading ? <LinearProgress /> : null}
                            <Button
                                type="submit"
                                disabled={loading}
                            >
                                {t('user.create-password.submit')}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </FormProvider>
        </Stack>
    );
}
